import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Send, ShieldCheck, Clock, Bitcoin, CheckCircle2, AlertCircle, Loader2, Lock,
  Copy, CopyCheck, RefreshCw, Timer, ArrowLeft,
} from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { getPublicSettings, createBooking, getBooking } from '../lib/api';
import { injectPixels, trackPixelEvent } from '../lib/pixels';

const SITE_LOGO =
  'https://customer-assets.emergentagent.com/job_87cd0f70-4f18-4348-a2fd-e2666a61734d/artifacts/ozrawt0t_IMG_0439.jpeg';

// Coins the customer can pick on /book-call. Network tag is shown for clarity.
const COINS = [
  { value: 'usdttrc20', label: 'USDT',     network: 'TRC-20',   sub: 'Tron · low fees · recommended' },
  { value: 'usdtbsc',   label: 'USDT',     network: 'BEP-20',   sub: 'BNB Chain · low fees' },
  { value: 'usdterc20', label: 'USDT',     network: 'ERC-20',   sub: 'Ethereum · high gas' },
  { value: 'btc',       label: 'Bitcoin',  network: 'BTC',      sub: 'Native Bitcoin network' },
  { value: 'eth',       label: 'Ethereum', network: 'ETH',      sub: 'Ethereum mainnet' },
  { value: 'bnbbsc',    label: 'BNB',      network: 'BEP-20',   sub: 'BNB Chain native' },
  { value: 'sol',       label: 'Solana',   network: 'SOL',      sub: 'Native Solana' },
  { value: 'ltc',       label: 'Litecoin', network: 'LTC',      sub: 'Fast & cheap' },
];

const PAY_LS_KEY = 'alvee_active_booking_payment';

function fmtCountdown(secs) {
  if (secs == null || secs < 0) return '00:00';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function statusPretty(s) {
  const t = (s || '').toLowerCase();
  if (['finished', 'confirmed', 'paid'].includes(t)) return { label: 'Payment confirmed', tone: 'success' };
  if (['confirming'].includes(t)) return { label: 'Confirming on blockchain…', tone: 'progress' };
  if (['sending'].includes(t)) return { label: 'Network broadcast…', tone: 'progress' };
  if (['partially_paid'].includes(t)) return { label: 'Partial payment received', tone: 'warn' };
  if (['failed', 'refunded', 'expired'].includes(t)) return { label: `Payment ${t}`, tone: 'error' };
  return { label: 'Waiting for crypto', tone: 'wait' };
}

export default function BookCallPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const payParam = params.get('pay');
  const cancelId = params.get('cancel');

  const [settings, setSettings] = useState(null);
  const [plan, setPlan] = useState('30min');
  const [form, setForm] = useState({
    name: '',
    email: '',
    contact: '',
    topic: '',
    preferred_datetime: '',
    pay_currency: 'usdttrc20',
  });
  const [submitting, setSubmitting] = useState(false);
  const [payment, setPayment] = useState(null); // { id, pay_address, pay_amount, pay_currency, ... }

  // ---- Load public settings + pixels ----
  useEffect(() => {
    (async () => {
      try {
        const s = await getPublicSettings();
        setSettings(s);
        injectPixels({
          facebook_pixel_id: s.facebook_pixel_id,
          tiktok_pixel_id: s.tiktok_pixel_id,
          google_ads_id: s.google_ads_id,
          google_analytics_id: s.google_analytics_id,
        });
      } catch (e) { console.error(e); }
    })();
  }, []);

  // ---- Refresh-safe: if ?pay=<bookingId> in URL, restore the payment widget ----
  useEffect(() => {
    let cancelled = false;
    async function tryRestore(id) {
      if (!id) return;
      try {
        const b = await getBooking(id);
        if (!cancelled && b?.pay_address) {
          setPayment(b);
          setPlan(b.plan || '30min');
        }
      } catch { /* ignore */ }
    }
    if (payParam) {
      tryRestore(payParam);
    } else if (typeof localStorage !== 'undefined') {
      try {
        const saved = localStorage.getItem(PAY_LS_KEY);
        if (saved) {
          const p = JSON.parse(saved);
          if (p?.id) {
            // Hydrate URL & restore
            setParams({ pay: p.id }, { replace: true });
            tryRestore(p.id);
          }
        }
      } catch { /* ignore */ }
    }
    return () => { cancelled = true; };
  }, []);

  // ---- Poll payment status every 6s when widget active ----
  const pollRef = useRef(null);
  useEffect(() => {
    if (!payment?.id) return;
    const terminal = ['paid', 'payment_failed', 'payment_expired', 'payment_refunded'];
    if (terminal.includes(payment.status)) return;
    pollRef.current = setInterval(async () => {
      try {
        const b = await getBooking(payment.id);
        setPayment((cur) => ({ ...(cur || {}), ...b }));
        if (b?.status === 'paid') {
          toast.success('Payment confirmed! ✅');
          trackPixelEvent('Purchase', {
            value: Number(b.amount_usd || 0), currency: 'USD',
            content_name: `book-call-${b.plan}`,
          });
          try { localStorage.removeItem(PAY_LS_KEY); } catch { /* ignore */ }
        }
      } catch { /* network blip — keep polling */ }
    }, 6000);
    return () => clearInterval(pollRef.current);
  }, [payment?.id, payment?.status]);

  // ---- Countdown timer (20 min from created_at) ----
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!payment?.created_at) return;
    const t = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => clearInterval(t);
  }, [payment?.created_at]);

  const secsLeft = (() => {
    if (!payment?.created_at) return null;
    const created = new Date(payment.created_at).getTime();
    const expiry = created + 20 * 60 * 1000;
    return Math.max(0, Math.floor((expiry - now) / 1000));
  })();

  // ---- Pricing ----
  const price = settings
    ? (plan === '30min'
      ? Number(settings.bookings_30min_price_usd || 150)
      : Number(settings.bookings_60min_price_usd || 290))
    : (plan === '30min' ? 150 : 290);

  function updateForm(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  function validate() {
    if (!form.name.trim()) return 'Please enter your name';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      return 'Please enter a valid email';
    if (!form.contact.trim()) return 'Please enter your WhatsApp / Telegram contact';
    if (!form.topic.trim() || form.topic.trim().length < 10)
      return 'Please briefly describe the topic (at least 10 characters)';
    if (!form.preferred_datetime.trim()) return 'Please mention a preferred date / time';
    if (!form.pay_currency) return 'Please choose a crypto to pay with';
    return null;
  }

  async function submitBooking() {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const res = await createBooking({ ...form, plan });
      trackPixelEvent('InitiateCheckout', { value: price, currency: 'USD', plan });
      if (res?.pay_address) {
        setPayment(res);
        try { localStorage.setItem(PAY_LS_KEY, JSON.stringify({ id: res.id })); } catch { /* ignore */ }
        setParams({ pay: res.id }, { replace: false });
        // Smooth scroll to widget after render
        setTimeout(() => {
          document.getElementById('pay-widget')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 300);
      } else if (res?.status === 'payment_gateway_unconfigured') {
        toast.error('Payment gateway not configured yet. Please contact us via Telegram.', { duration: 8000 });
      } else {
        toast.error(`Booking saved but no payment address — we'll contact you. ID: ${res?.id?.slice(0, 8)}`);
      }
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || e?.message || 'Failed to create payment';
      toast.error(msg);
    } finally { setSubmitting(false); }
  }

  function backToForm() {
    setPayment(null);
    try { localStorage.removeItem(PAY_LS_KEY); } catch { /* ignore */ }
    navigate('/book-call', { replace: true });
  }

  const enabled = settings?.bookings_enabled !== false;
  const title = settings?.bookings_page_title || 'Book a 1-on-1 Call with AlveeFx';
  const subtitle = settings?.bookings_page_subtitle ||
    'Direct consultation on trading setups, market psychology, and e-commerce business. Private. Focused. Actionable.';

  return (
    <main className="min-h-screen text-white bg-[hsl(0_0%_4%)] relative noise-overlay" data-testid="book-call-page">
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 opacity-50"
        style={{
          background:
            'radial-gradient(60% 60% at 50% 0%, rgba(225,29,46,0.16), transparent 70%), radial-gradient(50% 50% at 80% 100%, rgba(245,185,66,0.12), transparent 70%)',
        }}
      />
      <header className="relative z-10 border-b border-white/5">
        <div className="container-x py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <img src={SITE_LOGO} alt="AlveeFx" className="h-8 w-8 rounded-full object-cover ring-1 ring-[rgba(245,185,66,0.42)]" />
            <span className="font-display uppercase tracking-[0.18em] text-sm sm:text-base">AlveeFx</span>
          </div>
          <a href="/" className="text-xs text-muted-foreground hover:text-white transition-colors uppercase tracking-[0.18em]">
            Back to home
          </a>
        </div>
      </header>

      <section className="relative z-10 py-12 sm:py-16">
        <div className="container-x max-w-4xl mx-auto">
          {!payment && (
            <>
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[rgba(245,185,66,0.42)] bg-[rgba(245,185,66,0.08)] mb-4">
                  <span className="h-1.5 w-1.5 rounded-full bg-[hsl(43_92%_56%)] animate-pulse" />
                  <span className="text-[11px] uppercase tracking-[0.28em] text-[hsl(43_92%_56%)] font-semibold">1-on-1 Consultation</span>
                </div>
                <h1 className="font-display uppercase text-3xl sm:text-5xl lg:text-6xl leading-[1.02]" data-testid="book-call-title">{title}</h1>
                <p className="mt-5 text-base sm:text-lg text-[hsl(0_0%_82%)] leading-relaxed max-w-2xl mx-auto">{subtitle}</p>
              </div>

              {!enabled && (
                <Card className="card-surface border-[rgba(225,29,46,0.42)] mb-6" data-testid="bookings-disabled">
                  <CardContent className="p-5 text-center">
                    <AlertCircle className="h-8 w-8 text-[hsl(355_75%_60%)] mx-auto mb-2" />
                    <div className="font-semibold">Bookings are currently paused</div>
                    <div className="text-sm text-muted-foreground mt-1">Please check back later or contact us on Telegram.</div>
                  </CardContent>
                </Card>
              )}

              {/* Plan selection */}
              <div className="grid gap-4 sm:grid-cols-2 mb-8">
                {[
                  { key: '30min', title: '30-Min Consultation', price: Number(settings?.bookings_30min_price_usd || 150),
                    items: ['Focused setup review', 'Strategy Q&A', 'Quick action plan'] },
                  { key: '60min', title: '1-Hour Deep Session',  price: Number(settings?.bookings_60min_price_usd || 290),
                    items: ['Full strategy audit', 'Trading + e-commerce', 'Personalized roadmap', 'Follow-up notes'] },
                ].map((p) => (
                  <button key={p.key} type="button" onClick={() => setPlan(p.key)} data-testid={`plan-${p.key}`}
                    className={
                      'text-left rounded-2xl p-5 sm:p-6 transition-all border-2 ' +
                      (plan === p.key
                        ? 'border-[hsl(43_92%_56%)] bg-[rgba(245,185,66,0.06)] shadow-[0_18px_48px_-18px_rgba(245,185,66,0.45)]'
                        : 'border-white/10 bg-white/[0.02] hover:border-white/20')
                    }>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">
                        {p.key === '30min' ? 'Quick' : 'Deep dive'}
                      </span>
                      {plan === p.key && <CheckCircle2 className="h-4 w-4 text-[hsl(43_92%_56%)]" />}
                    </div>
                    <div className="font-display uppercase text-2xl">{p.title}</div>
                    <div className="mt-2 flex items-baseline gap-2">
                      <span className="font-display text-4xl text-[hsl(43_92%_56%)]">${p.price}</span>
                      <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground">USD · crypto</span>
                    </div>
                    <ul className="mt-4 space-y-1.5 text-sm text-[hsl(0_0%_82%)]">
                      {p.items.map((it, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[hsl(43_92%_56%)]" />
                          {it}
                        </li>
                      ))}
                    </ul>
                  </button>
                ))}
              </div>

              {/* Booking form */}
              <Card className="card-surface" data-testid="booking-form">
                <CardContent className="p-5 sm:p-7 space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="bc-name" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Full Name</Label>
                      <Input id="bc-name" value={form.name} onChange={(e) => updateForm('name', e.target.value)} placeholder="Your name" data-testid="field-name" disabled={!enabled} />
                    </div>
                    <div>
                      <Label htmlFor="bc-email" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Email</Label>
                      <Input id="bc-email" type="email" value={form.email} onChange={(e) => updateForm('email', e.target.value)} placeholder="you@email.com" data-testid="field-email" disabled={!enabled} />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="bc-contact" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">WhatsApp / Telegram</Label>
                    <Input id="bc-contact" value={form.contact} onChange={(e) => updateForm('contact', e.target.value)} placeholder="+880 17… or @username" data-testid="field-contact" disabled={!enabled} />
                  </div>
                  <div>
                    <Label htmlFor="bc-dt" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Preferred Date &amp; Time</Label>
                    <Input id="bc-dt" value={form.preferred_datetime} onChange={(e) => updateForm('preferred_datetime', e.target.value)} placeholder="e.g. Mon 24 Nov · 4:00 PM BD time" data-testid="field-datetime" disabled={!enabled} />
                    <div className="mt-1 text-[11px] text-muted-foreground">After payment, we&apos;ll confirm the exact slot via WhatsApp/Telegram.</div>
                  </div>
                  <div>
                    <Label htmlFor="bc-topic" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">What would you like to discuss?</Label>
                    <Textarea id="bc-topic" rows={4} value={form.topic} onChange={(e) => updateForm('topic', e.target.value)} placeholder="Trading setup, strategy review, risk management, e-commerce launch plan, etc." data-testid="field-topic" disabled={!enabled} />
                  </div>

                  {/* Coin selector */}
                  <div>
                    <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Pay With</Label>
                    <div className="grid gap-2 mt-1 sm:grid-cols-2" data-testid="coin-grid">
                      {COINS.map((c) => {
                        const active = form.pay_currency === c.value;
                        return (
                          <button
                            key={c.value}
                            type="button"
                            onClick={() => updateForm('pay_currency', c.value)}
                            disabled={!enabled}
                            data-testid={`coin-${c.value}`}
                            className={
                              'text-left rounded-md border px-3 py-2.5 transition flex items-center gap-3 ' +
                              (active
                                ? 'border-[hsl(43_92%_56%)] bg-[rgba(245,185,66,0.08)]'
                                : 'border-white/10 bg-white/[0.02] hover:border-white/20')
                            }
                          >
                            <div className="h-9 w-9 rounded-full bg-[rgba(245,185,66,0.10)] border border-[hsl(43_92%_56%)]/30 flex items-center justify-center shrink-0">
                              <Bitcoin className="h-4 w-4 text-[hsl(43_92%_56%)]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold flex items-center gap-1.5 flex-wrap">
                                <span>{c.label}</span>
                                <span className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-full bg-white/[0.05] border border-white/10 text-muted-foreground">{c.network}</span>
                              </div>
                              <div className="text-[11px] text-muted-foreground mt-0.5 truncate">{c.sub}</div>
                            </div>
                            {active && <CheckCircle2 className="h-4 w-4 text-[hsl(43_92%_56%)] shrink-0" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Total</div>
                      <div className="font-display text-3xl text-[hsl(43_92%_56%)]" data-testid="total-amount">${price}</div>
                    </div>
                    <Button size="lg" onClick={submitBooking} disabled={submitting || !enabled}
                      data-testid="submit-booking"
                      className="cta-sheen border-0 h-12 px-7 text-base font-semibold">
                      {submitting
                        ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating wallet…</>)
                        : (<><Send className="h-4 w-4 mr-2" /> Pay with crypto &amp; book</>)}
                    </Button>
                  </div>

                  <div className="text-[11px] text-muted-foreground flex items-start gap-2 pt-2">
                    <Lock className="h-3 w-3 mt-0.5 shrink-0" />
                    <span>Wallet address is generated by NOWPayments. You pay directly on-chain — we never touch your keys. Refunds only if the call cannot be delivered.</span>
                  </div>
                </CardContent>
              </Card>

              {/* Trust */}
              <div className="mt-6 grid gap-3 sm:grid-cols-3 text-center">
                {[
                  { Icon: ShieldCheck, label: 'Private & confidential' },
                  { Icon: Clock,       label: 'Scheduled within 24h' },
                  { Icon: Bitcoin,     label: 'On-site crypto checkout' },
                ].map((t, i) => (
                  <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-3 flex items-center justify-center gap-2 text-xs text-[hsl(0_0%_82%)]">
                    <t.Icon className="h-4 w-4 text-[hsl(43_92%_56%)]" />
                    {t.label}
                  </div>
                ))}
              </div>

              {cancelId && (
                <div className="mt-6 rounded-md border border-[rgba(225,29,46,0.42)] bg-[rgba(225,29,46,0.05)] p-4 text-sm text-center" data-testid="cancel-banner">
                  Payment was cancelled. You can try again above or pick a different crypto.
                </div>
              )}
            </>
          )}

          {/* INLINE PAYMENT WIDGET */}
          <AnimatePresence>
            {payment && (
              <motion.div
                key="pay-widget"
                id="pay-widget"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 16 }}
                transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
                data-testid="payment-widget"
              >
                <PaymentWidget payment={payment} secsLeft={secsLeft} onBack={backToForm} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      <footer className="relative z-10 border-t border-white/5 py-5 text-center">
        <p className="text-[11px] text-muted-foreground max-w-2xl mx-auto px-4 leading-snug">
          AlveeFx · Educational consultation only. Not financial advice.
          Refunds: only applicable if we cannot deliver the scheduled session.
        </p>
      </footer>
    </main>
  );
}

// ============================================================
//  Payment widget — wallet address + QR + amount + timer + status
// ============================================================
function PaymentWidget({ payment, secsLeft, onBack }) {
  const [copied, setCopied] = useState({ addr: false, amt: false });
  const status = statusPretty(payment.status === 'paid' ? 'paid' : (payment.payment_status || payment.status));
  const isPaid  = payment.status === 'paid' || ['finished', 'confirmed'].includes((payment.payment_status || '').toLowerCase());
  const isError = ['payment_failed', 'payment_expired', 'payment_refunded'].includes(payment.status || '');
  const expired = secsLeft != null && secsLeft <= 0 && !isPaid;

  const coin = (payment.pay_currency || '').toUpperCase();
  const amount = payment.pay_amount;
  const address = payment.pay_address || '';

  function doCopy(key, text) {
    if (!text) return;
    navigator.clipboard.writeText(String(text)).then(() => {
      setCopied((c) => ({ ...c, [key]: true }));
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied((c) => ({ ...c, [key]: false })), 1800);
    }).catch(() => toast.error('Copy failed — long-press to select'));
  }

  // QR payload: just the address (universal). Could also include amount (BIP-21 / wallet:address?amount=)
  // but wallet schemes differ widely so we stick to address-only for compatibility.
  const qrValue = address;

  // SUCCESS state
  if (isPaid) {
    return (
      <Card className="card-surface border-[rgba(70,200,120,0.55)]">
        <CardContent className="p-6 sm:p-8 text-center">
          <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.4 }}
            className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-[rgba(70,200,120,0.15)] border-2 border-[rgba(70,200,120,0.55)] mb-4">
            <CheckCircle2 className="h-9 w-9 text-[hsl(140_70%_60%)]" />
          </motion.div>
          <h2 className="font-display uppercase text-3xl mb-2">Payment Confirmed</h2>
          <p className="text-sm text-[hsl(0_0%_82%)] max-w-md mx-auto">
            Your <strong className="text-white">{payment.plan === '30min' ? '30-min' : '1-hour'}</strong> consultation is booked.
            We&apos;ll DM you on <strong className="text-white">{payment.contact}</strong> within 24h with the meeting link.
          </p>
          <div className="mt-5 inline-block px-4 py-2 rounded-md bg-white/[0.04] border border-white/10 font-mono text-xs">
            Booking ID: <span className="text-[hsl(43_92%_56%)]">{payment.id?.slice(0, 8)}</span>
          </div>
          <div className="mt-6">
            <Button onClick={onBack} variant="outline" className="border-white/10 bg-white/[0.02] gap-2">
              <ArrowLeft className="h-4 w-4" /> Back to home
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-surface" data-testid="payment-card">
      <CardContent className="p-5 sm:p-7">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
          <button type="button" onClick={onBack}
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-white transition-colors"
            data-testid="payment-back">
            <ArrowLeft className="h-3.5 w-3.5" /> Cancel &amp; restart
          </button>
          <div className={
            'inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.22em] font-semibold ' +
            (status.tone === 'success' ? 'bg-[rgba(70,200,120,0.12)] border border-[rgba(70,200,120,0.55)] text-[hsl(140_70%_70%)]'
             : status.tone === 'progress' ? 'bg-[rgba(108,176,255,0.10)] border border-[rgba(108,176,255,0.55)] text-[hsl(212_80%_75%)]'
             : status.tone === 'warn' ? 'bg-[rgba(245,185,66,0.10)] border border-[rgba(245,185,66,0.55)] text-[hsl(43_92%_72%)]'
             : status.tone === 'error' ? 'bg-[rgba(225,29,46,0.10)] border border-[rgba(225,29,46,0.55)] text-[hsl(0_72%_70%)]'
             : 'bg-white/[0.04] border border-white/10 text-[hsl(0_0%_82%)]')
          } data-testid="payment-status">
            {status.tone === 'progress' && <Loader2 className="h-3 w-3 animate-spin" />}
            {status.tone === 'wait' && <RefreshCw className="h-3 w-3 animate-spin" />}
            {status.label}
          </div>
        </div>

        {(isError || expired) && (
          <div className="mb-5 rounded-md border border-[rgba(225,29,46,0.55)] bg-[rgba(225,29,46,0.06)] p-3.5 text-sm flex items-start gap-2.5">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(0_72%_65%)]" />
            <div>
              <div className="font-semibold text-[hsl(0_72%_72%)]">
                {expired ? 'This payment window has expired' : `Payment ${payment.status?.replace('payment_', '')}`}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {expired
                  ? 'Crypto prices change quickly so we set a 20-minute window per address. Click "Cancel & restart" to generate a fresh wallet.'
                  : 'Please try again or contact us on Telegram if you already sent the funds.'}
              </div>
            </div>
          </div>
        )}

        <div className="grid gap-6 md:grid-cols-[260px,1fr] items-start">
          {/* QR */}
          <div className="flex flex-col items-center md:items-start gap-3">
            <div className="rounded-2xl bg-white p-3 shadow-[0_18px_60px_-18px_rgba(0,0,0,0.7)]" data-testid="payment-qr">
              <QRCodeSVG value={qrValue} size={220} level="M" includeMargin={false} bgColor="#FFFFFF" fgColor="#0A0A0A" />
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground text-center md:text-left">
              Scan with any crypto wallet
            </div>
          </div>

          {/* Details */}
          <div className="space-y-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-1">
                Send exactly
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => doCopy('amt', amount)}
                  className="group flex items-center gap-2 px-3 py-2 rounded-md bg-[rgba(245,185,66,0.08)] border border-[hsl(43_92%_56%)]/40 hover:bg-[rgba(245,185,66,0.14)] transition-colors"
                  data-testid="copy-amount"
                  title="Copy amount"
                >
                  <span className="font-display text-2xl sm:text-3xl text-[hsl(43_92%_56%)] tabular-nums">{amount}</span>
                  <span className="text-sm font-semibold text-white/85">{coin}</span>
                  {copied.amt ? <CopyCheck className="h-4 w-4 text-[hsl(140_70%_60%)]" /> : <Copy className="h-3.5 w-3.5 text-muted-foreground group-hover:text-white transition-colors" />}
                </button>
                <span className="text-xs text-muted-foreground">
                  ≈ ${payment.amount_usd} USD
                </span>
              </div>
              {payment.pay_network && (
                <div className="text-[11px] text-muted-foreground mt-1.5">
                  Network: <span className="text-[hsl(43_92%_72%)] font-semibold">{payment.pay_network}</span>
                </div>
              )}
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-1">
                To this wallet address
              </div>
              <div className="rounded-md border border-white/10 bg-white/[0.02] p-3 flex items-start gap-2">
                <code className="font-mono text-xs sm:text-sm text-white/90 break-all flex-1" data-testid="pay-address">
                  {address}
                </code>
                <button
                  type="button"
                  onClick={() => doCopy('addr', address)}
                  className="shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-md border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] transition-colors"
                  data-testid="copy-address"
                  title="Copy address"
                >
                  {copied.addr ? <CopyCheck className="h-4 w-4 text-[hsl(140_70%_60%)]" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-md border border-white/10 bg-white/[0.02] px-3 py-2.5">
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-1">
                  <Timer className="h-3 w-3" /> Expires in
                </div>
                <div className={'font-display text-xl tabular-nums mt-0.5 ' + (secsLeft != null && secsLeft < 120 ? 'text-[hsl(0_72%_65%)]' : 'text-white')} data-testid="pay-countdown">
                  {fmtCountdown(secsLeft)}
                </div>
              </div>
              <div className="rounded-md border border-white/10 bg-white/[0.02] px-3 py-2.5">
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Plan</div>
                <div className="text-sm font-semibold mt-0.5">{payment.plan === '30min' ? '30-Min Call' : '1-Hour Session'}</div>
              </div>
            </div>

            <div className="rounded-md border border-[hsl(43_92%_56%)]/30 bg-[rgba(245,185,66,0.06)] p-3.5 text-xs text-[hsl(0_0%_88%)] leading-relaxed">
              <div className="font-semibold text-[hsl(43_92%_72%)] mb-1.5 flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5" /> Before you send
              </div>
              <ul className="space-y-1 list-disc list-inside text-muted-foreground">
                <li>Send the <span className="text-white">exact amount</span> shown above — different amounts may not be detected automatically.</li>
                <li>Use the <span className="text-white">{payment.pay_network || coin}</span> network. Sending from the wrong chain will result in loss of funds.</li>
                <li>This page updates automatically once the network confirms — no need to email us.</li>
              </ul>
            </div>

            <div className="text-[11px] text-muted-foreground flex items-start gap-2">
              <Lock className="h-3 w-3 mt-0.5 shrink-0" />
              <span>Booking ID: <span className="font-mono">{payment.id?.slice(0, 8)}</span> · Powered by NOWPayments · Refunds: per our policy.</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
